"use client";

import type React from "react";
import { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useAppTheme } from "../../ThemeContext.tsx";

interface AddWebPagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (urls: string[]) => void;
}

export const AddWebPagesModal: React.FC<AddWebPagesModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [url, setUrl] = useState("");
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const handleSubmit = () => {
    if (!url.trim()) {
      toast({
        title: "URL is required",
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Simple URL validation
    try {
      new URL(url);
    } catch (e) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid URL including http:// or https://",
        status: "error",
        duration: 3000,
      });
      return;
    }

    onSave([url]);
    setUrl("");
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg={isDark ? "gray.800" : "white"}>
        <ModalHeader color={isDark ? "white" : "gray.800"}>
          Add Web Pages
        </ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel color={isDark ? "gray.300" : "gray.700"}>
                URL Address
              </FormLabel>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Enter URL"
                bg={isDark ? "gray.700" : "white"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                color={isDark ? "white" : "gray.800"}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            color={isDark ? "gray.300" : "gray.700"}
            _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
          >
            Cancel
          </Button>
          <Button
            colorScheme="gray"
            onClick={handleSubmit}
            _hover={{ bg: isDark ? "gray.600" : "gray.500" }}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
